import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';

import { apiGet, apiPath, apiPut, apiUpload } from '@/lib/api/client';
import { CubixApiError } from '@/lib/api/types';

import type { YieldCurveSnapshot } from './yieldCurveTypes';

const LIST_KEY = ['alm', 'yield-curves'] as const;
const itemKey = (id: string) => ['alm', 'yield-curves', id] as const;

/** GET /api/v1/alm/yield-curves — list snapshots (lightweight, no points). */
export function useYieldCurves(
  options?: Omit<UseQueryOptions<YieldCurveSnapshot[], CubixApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: LIST_KEY,
    queryFn: () => apiGet<YieldCurveSnapshot[]>(apiPath('alm', 'yield-curves')),
    ...options,
  });
}

/** GET /api/v1/alm/yield-curves/{id} — full snapshot incl. points. */
export function useYieldCurveDetail(
  snapshotId: string | undefined,
  options?: Omit<UseQueryOptions<YieldCurveSnapshot, CubixApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: snapshotId ? itemKey(snapshotId) : ['alm', 'yield-curves', '__none__'],
    enabled: Boolean(snapshotId),
    queryFn: () => apiGet<YieldCurveSnapshot>(apiPath('alm', 'yield-curves', snapshotId!)),
    ...options,
  });
}

interface UploadArgs {
  file: File;
  currency?: string;
  replace?: boolean;
}

/** POST /api/v1/alm/yield-curves/upload — multipart xlsx upload. */
export function useUploadYieldCurve(
  options?: UseMutationOptions<YieldCurveSnapshot, CubixApiError, UploadArgs>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, currency, replace }) =>
      apiUpload<YieldCurveSnapshot>(
        apiPath('alm', 'yield-curves', 'upload'),
        file,
        { currency, replace }),
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
      return options?.onSuccess?.(...args);
    },
  });
}

/** PUT /api/v1/alm/yield-curves/{id}/validate */
export function useValidateYieldCurve(
  options?: UseMutationOptions<YieldCurveSnapshot, CubixApiError, string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (snapshotId) =>
      apiPut<YieldCurveSnapshot, undefined>(
        apiPath('alm', 'yield-curves', snapshotId, 'validate'), undefined),
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
      qc.invalidateQueries({ queryKey: itemKey(args[1]) });
      return options?.onSuccess?.(...args);
    },
  });
}

/** PUT /api/v1/alm/yield-curves/{id}/archive */
export function useArchiveYieldCurve(
  options?: UseMutationOptions<YieldCurveSnapshot, CubixApiError, string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (snapshotId) =>
      apiPut<YieldCurveSnapshot, undefined>(
        apiPath('alm', 'yield-curves', snapshotId, 'archive'), undefined),
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
      qc.invalidateQueries({ queryKey: itemKey(args[1]) });
      return options?.onSuccess?.(...args);
    },
  });
}
